/* eslint-disable @angular-eslint/prefer-inject */
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import {
    SlotCustomFieldsService,
    SlotsService,
} from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    ICustomField,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { SlotStore } from './slot.store';
import {
    SlotCreate,
    SlotRead,
    SlotSearchParams,
} from '../../entities/slot.dto';
import { Operation } from 'fast-json-patch';

@Injectable()
export class SlotDataService extends AbstractDataService<SlotRead, SlotCreate> {
    private customFieldsService = inject(SlotCustomFieldsService);
    protected EntityName = PFWmsEntities.Slot;
    constructor(
        private service: SlotsService,
        store: SlotStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }
    protected createObs$(body: SlotCreate): Observable<SlotRead> {
        return this.service.slotCreate({
            slotViewModelCreateBody: body,
        }) as Observable<SlotRead>;
    }
    protected deleteObs$(id: string): Observable<SlotRead> {
        return this.service.slotDelete({
            id,
        }) as Observable<SlotRead>;
    }
    protected undoDeleteObs$(id: string): Observable<SlotRead> {
        return this.service.slotUndoDelete({
            id,
        }) as Observable<SlotRead>;
    }
    protected getObs$(id: string): Observable<SlotRead> {
        return this.service.slotReadById({
            id,
        }) as Observable<SlotRead>;
    }
    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<SlotRead> {
        return this.service.slotUpdatePatch({
            id,
            operation: operations,
        }) as Observable<SlotRead>;
    }
    protected searchObs$(
        searchParams: SlotSearchParams
    ): Observable<IPagedResult<SlotRead>> {
        return this.service.slotSearch(searchParams) as Observable<
            IPagedResult<SlotRead>
        >;
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<SlotRead>>> {
        return this.service.slotGetChanges(searchParams) as Observable<
            IPagedResult<EntityChangeTrackingDto<SlotRead>>
        >;
    }

    protected override createCustomFieldObs$(
        entityId: string,
        customField: ICustomField
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.slotCreateCustomField({
            entityId,
            customFieldViewModelCreate: {
                key: customField.key,
                value: customField.value,
            },
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override updateCustomFieldObs$(
        entityId: string,
        customFieldId: string,
        customField: ICustomField
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.slotCustomFieldsUpdatePut({
            entityId,
            id: customFieldId,
            customFieldViewModelCreate: {
                key: customField.key,
                value: customField.value,
            },
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override deleteCustomFieldObs$(
        entityId: string,
        customFieldId: string
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.slotDeleteCustomField({
            entityId,
            id: customFieldId,
        }) as Observable<ICustomField & { id: string }>;
    }
}
